import { memo, useCallback, useMemo } from 'react';
import { cn, Paginator } from '@divlab/divanui';

import * as ModalWindows from '@Stores/ModalWindows';
import * as CatalogFiltrator from '@Stores/CatalogFiltrator';
import * as Filtrator from '@Stores/Filtrator';
import { useDeps } from '@Contexts/DI';
import useSelectFilter from '@Queries/useSelectFilter';
import useTranslation from '@Queries/useTranslation';
import useNavigation from '@Navigation/useNavigation';
import Button from '@UI/Button';
import UILink from '@UI/Link';
import FavoritesFilters from '@Components/Filters/FavoritesFilters';
import Message from '@Components/Message';
import ProductCard from '../ProductCard';
import TypesFilter from '../TypesFilter';
import styles from './FavoritesCatalog.module.css';

import type { FC, MouseEvent } from 'react';
import type { UseInfiniteQueryResult, InfiniteData } from '@tanstack/react-query';
import type { GenerateUrl } from '@divlab/divanui';
import type { FavoritesData } from '@Types/Favorites';
import type { ProductData } from '@Types/Product';

export interface FavoritesCatalogProps {
  className?: string;
  favorites: UseInfiniteQueryResult<InfiniteData<FavoritesData>>;
  slug: string;
}

const FavoritesCatalog: FC<FavoritesCatalogProps> = (props) => {
  const { className, favorites, slug, ...restProps } = props;
  const { t } = useTranslation();
  const { pathname, search } = useNavigation();
  const { analytics } = useDeps();
  const filtrator = CatalogFiltrator.useFiltrator();
  const resetLink = CatalogFiltrator.useResetLink();
  const [firstPage] = favorites.data.pages;
  const lastPage = favorites.data.pages[favorites.data.pages.length - 1];
  const hasFilters = filtrator.filters?.length > 0 || filtrator.sort?.length > 0;
  const { selectFilter } = useSelectFilter(slug || '');
  const resetAllUrl = `${pathname}${resetLink}`;

  const products: ProductData[] = useMemo(() => {
    const allProducts: ProductData[] = [];
    favorites.data.pages.forEach((page) => {
      page.products.forEach((product) => {
        allProducts.push(product);
      });
    });

    return allProducts;
  }, [favorites.data]);

  const generatePaginatorUrl: GenerateUrl = useCallback(
    (params) => {
      const pageParam = params.page;
      const chunks = pathname.split('/');
      const lastChunk = chunks[chunks.length - 1];
      const newChunk = `page-${pageParam}`;
      const urlHasPage = lastChunk.match(/page-[0-9]{0,}/);

      if (pageParam < 2) {
        if (urlHasPage) delete chunks[chunks.length - 1];
      } else if (urlHasPage) {
        chunks[chunks.length - 1] = newChunk;
      } else {
        chunks.push(newChunk);
      }

      return `/${chunks.filter(Boolean).join('/')}${search}`;
    },
    [pathname, search],
  );

  const linkToNextPage = useMemo(() => {
    return generatePaginatorUrl({ page: lastPage.page + 1 });
  }, [generatePaginatorUrl, lastPage.page]);

  const handleClickMore = useCallback(async () => {
    if (favorites.isFetchingNextPage || !favorites.hasNextPage) return;

    await favorites.fetchNextPage();

    window.history.pushState({}, null, linkToNextPage);
  }, [favorites, linkToNextPage]);

  const handleOpenFilters = useCallback(
    (e: MouseEvent, selectedFilterId: string) => {
      ModalWindows.open('Filters', {
        selectedFilterId,
        entityId: 'catalog',
        onChangeFilters: selectFilter,
      });
    },
    [selectFilter],
  );

  const handleResetAllFilters = useCallback(() => {
    Filtrator.resetAll('catalog');
    const allAppliedFilters = Filtrator.getAppliedFiltersNames('catalog');

    analytics.dispatchEvent('filters.clear', {
      allAppliedFilters,
      label: 'Все фильтры',
      status: 'не закреп',
    });
  }, [analytics]);

  return (
    <div className={cn(styles.wrapper, className)} {...restProps}>
      {hasFilters && <TypesFilter />}
      <div className={styles.content}>
        {hasFilters && (
          <div className={styles.filters}>
            <FavoritesFilters count={firstPage.productsTotalCount} onOpen={handleOpenFilters} />
          </div>
        )}
        <div className={styles.catalog}>
          {products.length > 0 ? (
            <>
              <div className={styles.products}>
                {products.map((product) => {
                  return (
                    <ProductCard key={product.id} className={styles.product} product={product} />
                  );
                })}
              </div>
              {favorites.hasNextPage && (
                <div className={styles.moreWrapper}>
                  <Button
                    preventDefault
                    theme='dirty'
                    wide
                    className={styles.moreButton}
                    disabled={favorites.isFetchingNextPage}
                    waiting={favorites.isFetchingNextPage}
                    to={linkToNextPage}
                    onClick={handleClickMore}
                  >
                    {t('show-more')}
                  </Button>
                </div>
              )}

              {firstPage.pageCount > 1 && (
                <div className={styles.wrapperPaginator}>
                  <Paginator
                    now={lastPage.page}
                    total={firstPage.pageCount}
                    generateUrl={generatePaginatorUrl}
                  />
                </div>
              )}
            </>
          ) : (
            <Message
              className={styles.message}
              title={t('not-found.title')}
              text={
                <>
                  {t('not-found.try-change')}{' '}
                  <UILink
                    view='primary'
                    underlined
                    to={filtrator.withoutUrl ? undefined : resetAllUrl}
                    onClick={handleResetAllFilters}
                  >
                    {t('not-found.clear')}
                  </UILink>{' '}
                  {t('not-found.find-products')}
                </>
              }
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default memo(FavoritesCatalog);
